import { useEffect } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

// Actions
import { getSubdomain } from 'actions/common'
import { getClubConfig } from 'actions/link-in-bio'

// Helpers
import { isVideo, isUrlGif } from 'helpers/link-in-bio'
import { trackMetric } from 'helpers/metrics'
import { isReducedMotionEnabled } from 'helpers/common'

// Components
import Button from 'components/button/Button'
import Loading from 'components/loading/Loading'
import SocialLinks from 'components/link-in-bio/social-links/SocialLinks'

// Assets
import { ReactComponent as ScorePlay } from 'img/scoreplay.svg'
import imgPortlandPickles from 'img/portland_pickles.png'

// Styles
import styles from './LinkInBio.module.scss'

const showBg = url => {
  if (!url || (isReducedMotionEnabled() && isUrlGif(url))) return false
  return true
}

const StyledLinkInBio = styled.div`
  background-image: ${props =>
    showBg(props.bg) ? `url("${props.bg}")` : 'none'};
`

const LinkInBio = ({
  getSubdomain,
  subdomain,
  loading,
  error,
  getClubConfig,
  data,
}) => {
  useEffect(getSubdomain, [getSubdomain])

  useEffect(() => {
    subdomain && getClubConfig()
  }, [subdomain, getClubConfig])

  useEffect(() => {
    data?.name && data?.avatar_url && setMetaData(data.name, data.avatar_url)
  }, [data?.name, data?.avatar_url])

  if (!subdomain) return
  if (error) return <p className={styles.error}>{error}</p>
  if (loading || !data) return <Loading />

  const buttons = getSortedData(data.buttons)
  const socials = getSortedData(data.social)

  return (
    <StyledLinkInBio className={`bg ${styles.app}`} bg={data.bg_url}>
      {isVideo(data.bg_url) && (
        <div className={`video ${styles.videoContainer}`}>
          <video
            className={styles.video}
            autoPlay
            muted
            type="video/mp4"
            src={data.bg_url}
            loop
            playsInline
          ></video>
          <div className={`video-overlay ${styles.videoOverlay}`} />
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.content}>
          <header className={styles.header}>
            {data.avatar_url && (
              <img
                src={
                  subdomain === 'wearecollide'
                    ? imgPortlandPickles
                    : data.avatar_url
                }
                className={styles.logo}
                alt={data.name}
                height="70"
              />
            )}
            {data.name && (
              <h1 className={styles.name}>
                {subdomain === 'wearecollide' ? 'Portland Pickles' : data.name}
              </h1>
            )}
            {data.insta_handle && (
              <h2 className={styles.handle}>{data.insta_handle}</h2>
            )}
          </header>

          <section className={styles.links}>
            {buttons.map((btn, i) => {
              const customClasses = `btn btn-${i + 1}`
              const trackingLabel = `button_${i + 1}_${btn.text
                .replace(/ /g, '_')
                .toLowerCase()}`
              const isYouTube = btn.url.includes('youtube.com/embed')

              if (isYouTube) {
                return (
                  <iframe
                    className={styles.youTube}
                    src={btn.url}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                )
              }

              return (
                <Button
                  url={btn.url}
                  key={`btn-${i}`}
                  className={customClasses}
                  onClick={e => trackMetric(trackingLabel, e)}
                >
                  {btn.text}
                  {btn.sub_text && <span>{btn.sub_text}</span>}
                </Button>
              )
            })}
          </section>

          <SocialLinks socials={socials} />

          <footer className={styles.footer}>
            <a
              href="https://www.scoreplay.io/fans"
              className={styles.scoreplay}
              onClick={e => trackMetric('link_scoreplay', e)}
              target="_blank"
              rel="noreferrer"
            >
              <ScorePlay />
            </a>
          </footer>
        </div>
      </div>
    </StyledLinkInBio>
  )
}

const mapStateToProps = state => ({
  ...state.common,
  data: state.linkInBio.data,
})

const mapDispatchToProps = dispatch => ({
  getSubdomain: () => dispatch(getSubdomain()),
  getClubConfig: () => dispatch(getClubConfig()),
})

export default connect(mapStateToProps, mapDispatchToProps)(LinkInBio)

const setMetaData = (name, avatar) => {
  let title = `${name} — scoreplay.fan`
  document.title = title
  let favicon = document.getElementById('favicon')
  favicon.href = avatar
}

const getSortedData = data => {
  return [...data].sort((a, b) => {
    return b.position - a.position || b.ID - a.ID
  })
}
